import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ShoppingBag } from 'lucide-react';
import api from '@/lib/api';
import SafeImg from './SafeImg';

const won = (n) => `${(Number(n) || 0).toLocaleString('ko-KR')}원`;

/**
 * NowSellingCard — self-contained "🔴 지금 소개중" card that polls
 * /api/public/broadcasts/{broadcastId}/active-product every `pollMs` ms
 * (default 4s) and shows the active product with a 바로구매 CTA.
 *
 * Renders NOTHING when there is no broadcastId or no active product,
 * so it can safely sit in any layout without leaving an empty box.
 *
 * variant="hero"   → red-gradient full-width hero card (consumer live page).
 * variant="inline" → borderless card that fits inside seller profile.
 */
const NowSellingCard = ({ broadcastId, variant = 'inline', pollMs = 4000, sellerId, title }) => {
  const [data, setData] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const timer = useRef(null);

  useEffect(() => {
    if (!broadcastId) { setData(null); setLoaded(true); return; }
    let cancelled = false;
    const poll = async () => {
      try {
        const res = await api.get(`/public/broadcasts/${broadcastId}/active-product`);
        if (!cancelled) { setData(res.data); setLoaded(true); }
      } catch { if (!cancelled) { setData(null); setLoaded(true); } }
    };
    poll();
    timer.current = setInterval(poll, pollMs);
    return () => { cancelled = true; if (timer.current) clearInterval(timer.current); };
  }, [broadcastId, pollMs]);

  // Nothing to show until we've actually confirmed there's an active product.
  if (!broadcastId || !loaded) return null;
  const ap = data?.active_product;
  if (!ap) return null;

  const commonLabel = (
    <p className="text-xs font-black inline-flex items-center gap-1 text-[#FF3B30] mb-3">
      <span className="inline-block w-2 h-2 rounded-full bg-[#FF3B30] animate-pulse" /> 지금 소개중
    </p>
  );

  if (variant === 'hero') {
    // Full-width consumer-facing hero (used on /live/{broadcastId})
    return (
      <div className="rounded-3xl border-2 border-[#FF3B30] shadow-lg bg-white dark:bg-[#141414] p-5" data-testid="active-panel">
        {commonLabel}
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4" data-testid="active-product-card">
          <div className="flex items-center gap-3 sm:flex-1 sm:min-w-0 w-full">
            {ap.slot_number != null && (
              <span className="w-10 h-10 rounded-full bg-[#FF3B30] text-white text-base font-black flex items-center justify-center shrink-0" data-testid="active-product-slot">{ap.slot_number}</span>
            )}
            <SafeImg src={ap.image} kind="product" className="w-20 h-20 rounded-lg object-cover shrink-0" alt="" />
            <div className="flex-1 min-w-0">
              <p className="text-base font-black truncate" data-testid="active-product-name">{ap.name}</p>
              <p className="text-lg font-black text-[#FF3B30]" data-testid="active-product-price">{won(ap.sale_price)}</p>
            </div>
          </div>
          <Link
            to={`/product/${ap.product_id}?from=live&broadcast=${broadcastId}`}
            className="w-full sm:w-auto px-5 py-3 rounded-full bg-black text-white text-sm font-bold hover:bg-[#FF3B30] transition-colors inline-flex items-center justify-center gap-1"
            data-testid="active-product-buy">
            <ShoppingBag size={14} /> 바로구매
          </Link>
        </div>
      </div>
    );
  }

  // 'inline' — used inside seller profile between 오늘 방송 and 오늘 라이브 상품.
  return (
    <section data-testid="profile-now-selling">
      <h2 className="text-2xl font-black tracking-tight mb-3 inline-flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
        <span className="inline-block w-2.5 h-2.5 rounded-full bg-[#FF3B30] animate-pulse" /> 지금 소개중
      </h2>
      <div className="rounded-2xl border-2 border-[#FF3B30] bg-white dark:bg-[#141414] shadow-md p-5" data-testid="active-panel">
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
          <div className="flex items-center gap-3 sm:flex-1 sm:min-w-0 w-full">
            {ap.slot_number != null && (
              <span className="w-10 h-10 rounded-full bg-[#FF3B30] text-white text-base font-black flex items-center justify-center shrink-0" data-testid="active-product-slot">{ap.slot_number}</span>
            )}
            <SafeImg src={ap.image} kind="product" className="w-20 h-20 rounded-lg object-cover shrink-0" alt="" />
            <div className="flex-1 min-w-0">
              <p className="text-base font-black truncate" data-testid="active-product-name">{ap.name}</p>
              <p className="text-lg font-black text-[#FF3B30]" data-testid="active-product-price">{won(ap.sale_price)}</p>
            </div>
          </div>
          <Link
            to={`/product/${ap.product_id}?from=live&broadcast=${broadcastId}`}
            className="w-full sm:w-auto px-5 py-3 rounded-full bg-black text-white text-sm font-bold hover:bg-[#FF3B30] transition-colors inline-flex items-center justify-center gap-1"
            data-testid="active-product-buy">
            <ShoppingBag size={14} /> 바로구매
          </Link>
        </div>
      </div>
    </section>
  );
};

export default NowSellingCard;
